import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarDays } from "lucide-react";
import api from "../lib/api";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import { formatDateTime, countdown } from "../lib/format";

export default function Meetings() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const [m, mem] = await Promise.all([api.get("/meetings"), api.get("/members")]);
    setMeetings(m.data);
    setMembers(mem.data);
  };
  useEffect(() => {
    document.title = "Meetings · The Synergy Table";
    load();
  }, []);

  const now = new Date();
  const upcoming = meetings.filter((m) => new Date(m.date) >= now);
  const past = meetings.filter((m) => new Date(m.date) < now);

  return (
    <div className="space-y-10" data-testid="meetings-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline">Rhythm</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Meetings</h1>
          <p className="text-[#B8B3AC] mt-3 max-w-xl">Every month, we sit at the table. Agenda, decisions, actions — all logged.</p>
        </div>
        {user.role === "admin" && (
          <button onClick={() => setShowNew(true)} data-testid="new-meeting" className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm self-start">
            <Plus size={16} /> Schedule meeting
          </button>
        )}
      </div>

      <section>
        <div className="overline mb-4">Upcoming</div>
        <div className="space-y-4" data-testid="upcoming-meetings">
          {upcoming.map((m) => (
            <Link key={m.id} to={`/meetings/${m.id}`} className="ts-card p-7 flex flex-col sm:flex-row sm:items-center gap-6 hover:border-[#C9A84C]/55 transition block">
              <div className="flex-1 min-w-0">
                <div className="font-serif text-2xl text-[#F0EDE8]">{m.title}</div>
                <div className="text-sm text-[#9A9590] mt-1">{formatDateTime(m.date)}</div>
                <div className="text-xs text-[#9A9590]/80 mt-1">{m.location || "Location TBD"}</div>
              </div>
              <div className="flex -space-x-2">
                {(m.attendees || []).slice(0, 6).map((a) => (
                  <Avatar key={a.id} name={a.name} photoUrl={a.photo_url} color={a.avatar_color} size={32} className="ring-2 ring-[#1A1A1A]" />
                ))}
              </div>
              <div className="text-2xl font-serif text-[#C9A84C] whitespace-nowrap">{countdown(m.date)}</div>
            </Link>
          ))}
          {upcoming.length === 0 && <div className="text-sm text-[#9A9590]">No meetings scheduled.</div>}
        </div>
      </section>

      <section>
        <div className="overline mb-4">Past meetings</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="past-meetings">
          {past.map((m) => (
            <Link key={m.id} to={`/meetings/${m.id}`} className="ts-card p-6 flex items-center gap-5 hover:border-[#C9A84C]/40 transition">
              <div className="w-12 h-12 rounded-md bg-[#0D0D0D] gold-border flex items-center justify-center text-[#C9A84C]">
                <CalendarDays size={20} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#F0EDE8] truncate">{m.title}</div>
                <div className="text-xs text-[#9A9590] mt-0.5">{formatDateTime(m.date)}</div>
              </div>
            </Link>
          ))}
          {past.length === 0 && <div className="text-sm text-[#9A9590]">No past meetings yet.</div>}
        </div>
      </section>

      {showNew && <NewMeetingModal members={members} onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewMeetingModal({ members, onClose, onCreated }) {
  const [f, setF] = useState({ title: "", date: "", location: "", agenda: "", attendee_ids: members.map(m => m.id) });
  const toggle = (id) => setF((p) => ({ ...p, attendee_ids: p.attendee_ids.includes(id) ? p.attendee_ids.filter(x => x!==id) : [...p.attendee_ids, id] }));
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/meetings", f);
    onClose(); onCreated();
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="ts-card p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        <div className="overline">New meeting</div>
        <h2 className="font-serif text-2xl text-[#F0EDE8] mt-2 mb-6">Schedule meeting</h2>
        <form onSubmit={submit} className="space-y-5">
          <div><label className="overline">Title</label><input required className="ts-input" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} /></div>
          <div><label className="overline">Date &amp; time</label><input required type="datetime-local" className="ts-input" value={f.date} onChange={(e)=>setF({...f,date:new Date(e.target.value).toISOString()})} /></div>
          <div><label className="overline">Location</label><input className="ts-input" value={f.location} onChange={(e)=>setF({...f,location:e.target.value})} /></div>
          <div><label className="overline">Agenda</label><textarea className="ts-input min-h-[80px] py-2" value={f.agenda} onChange={(e)=>setF({...f,agenda:e.target.value})} /></div>
          <div>
            <label className="overline">Attendees</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {members.map(m=>(<button type="button" key={m.id} onClick={()=>toggle(m.id)} className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full transition ${f.attendee_ids.includes(m.id)?"bg-[#C9A84C] text-[#0D0D0D]":"border border-[#C9A84C]/25 text-[#9A9590]"}`}>{m.name}</button>))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className="text-sm text-[#9A9590]">Cancel</button><button type="submit" className="btn-gold rounded-md px-5 py-2.5 text-sm">Schedule</button></div>
        </form>
      </div>
    </div>
  );
}
